import { HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Currency, Member } from '../../models';
import { ApiService, JwtService } from '../../utils';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BuildingService } from '../booking/building.service';
import { OAuthService } from 'angular-oauth2-oidc';

@Injectable()
export class MemberService {
  public currentUserSubject = new BehaviorSubject<Member>({} as Member);
  public currentUser: Observable<Member> = this.currentUserSubject.asObservable().pipe(distinctUntilChanged());

  public internalRegistrationUrl = 'first-connection';

  private readonly browserLang;

  private path = 'members';

  constructor(
    private buildingService: BuildingService,
    private apiService: ApiService,
    private jwtService: JwtService,
    private router: Router,
    private translateService: TranslateService,
    private oauthService: OAuthService,
  ) {
    this.browserLang = this.translateService.getBrowserLang();
  }

  /**
   * Set in the application the workplace of the current user
   */
  private static setUserWorkplaceData(userData, userWorkplace) {
    userData.workplace = userWorkplace;

    localStorage.setItem('userCurrency', JSON.stringify(userData.country.currencies));
    localStorage.setItem('userCountry', JSON.stringify(userData.country));
    localStorage.setItem('currentUser', JSON.stringify(userData));

    return userData;
  }

  /**
   * Removes the specified role from the specified user
   *
   * @param member the member whose roles must be reduced
   * @param role the role to remove
   */
  demote(member: Member, role: string): Observable<Member> {
    const body = {
      role,
    };

    return this.apiService.delete(`internal_members/${member.id}/roles`, body)
      .pipe(map(data => new Member(data)));
  }

  extractRolesAttributionDates(): Observable<any> {
    return this.apiService.download(`internal_members/extract/date-attribution-roles`);
  }

  getCurrentUser(): Member {
    return this.currentUserSubject.value;
  }

  getUser(): Member {
    return new Member(JSON.parse(localStorage.getItem('currentUser')));
  }

  getUserCurrency(): Currency {
    return JSON.parse(localStorage.getItem('userCurrency'))[0];
  }

  /**
   * Try to get the current user information an manage new arrivals.
   * This runs once when the user is authenticated and the application startup.
   *
   * @param uuid;
   */
  populate(uuid: string) {
    this.apiService.get(`${this.path}/${uuid}`)
      .subscribe(
        data => {
          if (data.workplace === null) {
            this.router.navigate([`/auth/${this.internalRegistrationUrl}`, data.id]);
          } else {
            this.setUserData(data);
          }
        },
        err => {
          if (this.apiService.internalSignUpNeeded(err)) {
            this.router.navigate([`/auth/${this.internalRegistrationUrl}`, '']);
          } else {
            this.purgeUser();
          }
        },
      );
  }

  /**
   * Adds the specified role to the specified user
   *
   * @param member the member whose roles must be enhanced
   * @param role the role to add
   */
  promote(member: Member, role: string) {
    const body = {
      role,
    };

    return this.apiService.post(`internal_members/${member.id}/roles`, body)
      .pipe(map(data => new Member(data)));
  }

  purgeUser() {
    this.jwtService.destroyToken();

    this.currentUserSubject.next({} as Member);

    localStorage.removeItem('userCurrency');
    localStorage.removeItem('userCountry');
    localStorage.removeItem('currentUser');
  }

  searchByNameAndEmail(search: string, isRegistered = true): Observable<Member[]> {
    if (search === '') {
      return of([]);
    }

    let params = new HttpParams().set('search', search);
    params = params.set('isRegistered', `${isRegistered}`);

    return this.apiService.get('members/search-by-name-and-email', params)
      .pipe(map(data => data.map(obj => new Member(obj))));
  }

  /**
   * Update the user on the server (email, pass, etc)
   *
   * @param resourceType
   * @param member
   *
   * @return
   */
  updateCurrent(resourceType, member): Observable<Member> {
    return this.apiService
      .put(`${resourceType}/${member.id}`, member)
      .pipe(map(data => {
        this.setUserData(data);
        return data;
      }));
  }

  public hasRole(member: Member, role: string): boolean {
    return member.roles.some((mRole) => mRole === role);
  }

  public get(id: string): Observable<Member> {
    return this.apiService.getSave(`${this.path}/${id}`);
  }

  /**
   * Enrichs the base data of the current user with additional API calls
   *
   * Return a stream to subscribe.
   *
   * @param userData
   */
  private fetchFurtherData(userData): Observable<any> {
    return this.buildingService.get(userData.workplace['id'])
      .pipe(
        map(userWorkplace => userData = MemberService.setUserWorkplaceData(userData, userWorkplace)),
      );
  }

  /**
   * Returns the preferred_language of the user (ldap value)
   * else returns the navigator language else english value
   *
   * @param data
   */
  private getPreferredLangOrDefault(data) {
    if (data.preferred_language) {
      const preferredLanguage = data.preferred_language.toLocaleLowerCase();
      return this.browserLang.match(preferredLanguage) ? preferredLanguage : 'en';
    } else {
      const availableLanguages = this.translateService.getLangs().join('|');
      return this.browserLang.match(availableLanguages) ? this.browserLang : 'en';
    }
  }

  /**
   * Set in the application all the information
   * depending on the user to populate.
   *
   * @param userData
   */
  private setUserData(userData: any) {
    const lang = this.getPreferredLangOrDefault(userData);
    this.translateService.use(lang);

    this.jwtService.saveToken(this.oauthService.getAccessToken());

    this.fetchFurtherData(userData).subscribe(completedUserData => {
      this.currentUserSubject.next(new Member(completedUserData));
    });
  }
}
